import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardFooter,
    Button,
    Input,
    Label,
    Avatar,
    AvatarImage,
    AvatarFallback,
    Separator,
    Skeleton,
} from '@ui';
import { useUserStore } from '@/stores/userStore';
import { useGetMe, useUpdateMe } from '@hooks/generated/me';
import { useUploadFile } from '@hooks/generated/file';
import { User, Mail, Phone, Camera, Save, Loader2, FileText } from 'lucide-react';
import { uploadFile } from '@/api';
import { toastMessage } from '@ui/components/Toast';
import type { ProfileFormData, CurrentUser } from '../../types';

export function ProfileTab() {
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { user, setUser, setError, clearError } = useUserStore();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    // Profile data
    const { data: meData, isLoading: isLoadingMe, refetch: refetchMe } = useGetMe({
        request: {},
        options: { enabled: true },
    });

    // Mutations
    const updateProfileMutation = useUpdateMe({
        options: {
            onSuccess: (response) => {
                const userData = response.data?.data;
                if (userData) {
                    setUser(userData);
                }
                clearError();
                toastMessage({ message: '프로필 정보가 수정 되었습니다.', type: 'success' });
                refetchMe();
            },
            onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
                const errorMessage = err.response?.data?.message || err.message || '프로필 업데이트에 실패했습니다.';
                setError(errorMessage);
                toastMessage({ message: errorMessage, type: 'error' });
            },
        },
    });

    const uploadFileMutation = useUploadFile({
        options: {
            onSuccess: (response) => {
                const imageUrl = response.data?.data?.fileUrl || '';
                setUser({ ...user, profileImageUrl: imageUrl });
            },
        },
    });

    const currentUser = (meData || user) as CurrentUser | null;

    // Profile form
    const profileForm = useForm<ProfileFormData>({
        defaultValues: {
            name: currentUser?.name || '',
            phone: currentUser?.phone || '',
            note: currentUser?.note || '',
        },
    });

    // 프로필 데이터 로드 시 폼 업데이트
    useEffect(() => {
        if (currentUser) {
            profileForm.reset({
                name: currentUser.name || '',
                phone: currentUser.phone || '',
                note: currentUser.note || '',
            });
        }
    }, [currentUser, profileForm]);

    // 파일 선택 시 핸들러
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];

        if (!file.type.startsWith("image/")) {
            setUploadError("이미지 파일만 업로드 가능합니다.");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setUploadError("파일 크기는 5MB 이하여야 합니다.");
            return;
        }

        setIsUploading(true);
        setUploadError(null);

        try {
            const result = await uploadFile(file);

            if (result.success) {
                setUser({
                    ...user,
                    profileImageUrl: result.fileUrl,
                });
                setPreviewUrl(result.fileUrl);

                await updateProfileMutation.mutate({
                    userUpdateRequest: {
                        ...user,
                        profileImageUrl: result.fileUrl,
                    },
                });
            } else {
                setUploadError("이미지 업로드에 실패했습니다.");
            }
        } catch (error) {
            console.error("File upload error:", error);
            setUploadError("이미지 업로드 중 오류가 발생했습니다.");
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleImageButtonClick = (e: React.MouseEvent) => {
        e.preventDefault();
        fileInputRef.current?.click();
    };

    const onSubmitProfile = (data: ProfileFormData) => {
        updateProfileMutation.mutate({
            userUpdateRequest: {
                name: data.name,
                phone: data.phone || undefined,
                note: data.note || undefined,
            },
        });
    };

    if (isLoadingMe) {
        return (
            <div className="mx-auto max-w-2xl space-y-6">
                <Card>
                    <CardContent className="p-6 space-y-4">
                        <Skeleton className="h-24 w-24 rounded-full" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-2xl space-y-6">
            <Card>
                <form onSubmit={profileForm.handleSubmit(onSubmitProfile)}>
                    <CardHeader>
                        <CardTitle>매니저 정보</CardTitle>
                        <CardDescription>프로필 사진과 개인 정보를 수정할 수 있습니다.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* 프로필 사진 섹션 */}
                        <div className="flex items-center gap-6">
                            <div className="relative">
                                <Avatar className="h-24 w-24">
                                    <AvatarImage src={previewUrl || currentUser?.profileImageUrl} alt={currentUser?.name} />
                                    <AvatarFallback className="bg-orange-100 text-orange-600 text-2xl">
                                        {currentUser?.name?.charAt(0) || 'U'}
                                    </AvatarFallback>
                                </Avatar>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                <button
                                    type="button"
                                    onClick={handleImageButtonClick}
                                    disabled={uploadFileMutation.isPending}
                                    className={`absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full shadow-lg transition-transform ${uploadFileMutation.isPending
                                        ? 'bg-neutral-400 cursor-not-allowed'
                                        : 'bg-orange-500 hover:scale-110'
                                        } text-white`}
                                >
                                    {uploadFileMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Camera className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            <div>
                                <p className="font-medium text-neutral-800 dark:text-neutral-100">{currentUser?.name}</p>
                                <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">{currentUser?.email}</p>
                                <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                                    {uploadFileMutation.isPending ? '업로드 중...' : '카메라 아이콘을 클릭하여 사진 변경 (JPG, GIF, PNG 최대 2MB)'}
                                </p>
                            </div>
                        </div>

                        <Separator />

                        {/* 기본 정보 섹션 */}
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">이메일</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
                                    <Input
                                        id="email"
                                        type="email"
                                        value={currentUser?.email || ''}
                                        disabled
                                        className="pl-10 bg-neutral-50 dark:bg-neutral-800"
                                    />
                                </div>
                                <p className="text-xs text-neutral-400 dark:text-neutral-500">이메일은 변경할 수 없습니다.</p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="name">이름</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
                                    <Input
                                        id="name"
                                        type="text"
                                        placeholder="이름을 입력하세요"
                                        className="pl-10"
                                        {...profileForm.register('name', {
                                            required: '이름을 입력해주세요.',
                                            minLength: { value: 2, message: '이름은 최소 2자 이상이어야 합니다.' },
                                        })}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone">전화번호</Label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
                                    <Input
                                        id="phone"
                                        type="tel"
                                        placeholder="010-1234-5678"
                                        className="pl-10"
                                        {...profileForm.register('phone', {
                                            pattern: {
                                                value: /^01[0-9]-?[0-9]{4}-?[0-9]{4}$/,
                                                message: '유효한 전화번호를 입력해주세요.',
                                            },
                                        })}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="note">메모</Label>
                                <div className="relative">
                                    <FileText className="absolute left-3 top-3 h-4 w-4 text-neutral-400 dark:text-neutral-500" />
                                    <textarea
                                        id="note"
                                        placeholder="메모를 입력하세요"
                                        className="flex min-h-[80px] w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        {...profileForm.register('note')}
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="border-t pt-6">
                        <Button type="submit" disabled={!profileForm.formState.isDirty || updateProfileMutation.isPending}>
                            {updateProfileMutation.isPending ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />저장 중...</>
                            ) : (
                                <><Save className="mr-2 h-4 w-4" />변경사항 저장</>
                            )}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}
